import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, LinearProgress, TextField, Tooltip } from '@mui/material'
import { ArrowBackRounded, ArrowForwardRounded, PrintRounded } from '@mui/icons-material'
import Logo from '../../../../assets/img/dostyq-online.png'
import Breadcrumb from '../../../../components/Breadcrumb'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import { useAppSelector } from '../../../../app/hooks'
import { userInfo } from '../../../../features/user/userSlice'
import { capitalize } from '../../../../utils/functions'
import { ui } from '../../../../utils/uiTokens'
import { curatorStudentsApiSlice } from '../../../../features/curator/curatorStudents'
import { useHeadScope } from '../../../head/headScope'
import ReportStudentCard from './ReportStudentCard'
import { LAST_REPORT_KEY } from '../RatingPage'

const PRACTICE_LABEL = {
    WITH_CAMERA: 'withCamera',
    WITHOUT_CAMERA: 'withoutCamera',
    ABSENT: 'absent',
}

const StudentReportPage = () => {
    const { formatMessage, locale } = useIntl()
    const { userSubjectEnrollId } = useParams()
    const [searchParams] = useSearchParams()
    const user = useAppSelector(userInfo)
    // басшы только читает; ментор в шапке - ментор группы ученика, а не тот, кто открыл
    const { readOnly, basePath } = useHeadScope()
    const navigate = useNavigate()
    const { data: headMentors = [] } = curatorStudentsApiSlice.useFetchHeadMentorsQuery(undefined, { skip: !readOnly })

    const monthNumber = Number(searchParams.get('monthNumber')) || 0
    const weekNumber = Number(searchParams.get('weekNumber')) || 0

    const { data: report, isFetching } = ratingApiSlice.useGetWeekReportQuery({
        userSubjectEnrollId: +userSubjectEnrollId,
        monthNumber,
        weekNumber,
    })
    // Тот же список, что в рейтинге: группа, период, поиск, порядок по орнам
    const { data: siblingsResponse } = ratingApiSlice.useGetStudentRatingQuery({
        groupId: Number(searchParams.get('groupId')) || 0,
        monthNumber,
        weekNumber,
        filter: searchParams.get('filter') || '',
        page: 0,
        size: 1000,
    })
    const siblings = siblingsResponse?.data || []
    const position = siblings.findIndex((s) => s.userSubjectEnrollId === +userSubjectEnrollId)
    const prev = position > 0 ? siblings[position - 1] : null
    const next = position >= 0 && position < siblings.length - 1 ? siblings[position + 1] : null
    // replace: стрелка «назад» ведёт в рейтинг, а не по всем открытым отчётам
    const goTo = (id: number) => {
        navigate(`${basePath}/rating/${id}?${searchParams.toString()}`, { replace: true })
        window.scrollTo(0, 0)
    }

    useEffect(() => {
        sessionStorage.setItem(LAST_REPORT_KEY, String(userSubjectEnrollId))
    }, [userSubjectEnrollId])

    const [saveWeekScore, { isLoading: isSaving }] = ratingApiSlice.useSaveWeekScoreMutation()

    const [zachetInput, setZachetInput] = useState('')
    const [commentInput, setCommentInput] = useState('')

    useEffect(() => {
        setZachetInput(
            report?.weekScore?.zachetPoint != null ? String(report.weekScore.zachetPoint) : '',
        )
        setCommentInput(report?.weekScore?.mentorComment || '')
    }, [report])

    if (isFetching) return <LinearProgress />
    if (!report) return null

    const summary = report.summary

    const mentorName = readOnly

        ? headMentors

              .filter((mentor) => mentor.groupTitle === summary.groupTitle)

              .map((mentor) => `${mentor.firstName} ${mentor.lastName}`)

              .join(', ')

        : user?.firstName ? `${user.firstName} ${user.lastName}` : ''
    const score = report.weekScore

    const basePayload = {
        userSubjectEnrollId: +userSubjectEnrollId,
        monthNumber,
        weekNumber,
        zachetPoint: score?.zachetPoint ?? null,
        practiceStatus: score?.practiceStatus || '',
        // Sent back unchanged: an omitted slot arrives empty and the server clears it.
        practice2Status: score?.practice2Status || '',
        zachetComment: score?.zachetComment || '',
        mentorComment: score?.mentorComment || '',
    }

    const saveZachet = async () => {
        const trimmed = zachetInput.trim()
        try {
            await saveWeekScore({
                ...basePayload,
                zachetPoint: trimmed === '' ? null : Number(trimmed),
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
        } catch (error) {}
    }

    const saveComment = async () => {
        try {
            await saveWeekScore({
                ...basePayload,
                mentorComment: commentInput,
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
        } catch (error) {}
    }
    const videoPoint = summary.videoPoint + summary.conspectPoint
    const period = [
        monthNumber ? `${monthNumber} ${formatMessage({ id: 'month' })}` : '',
        weekNumber ? `${weekNumber} ${formatMessage({ id: 'week' })}` : '',
    ]
        .filter(Boolean)
        .join(' · ')

    const cards = [
        { id: 'video', value: summary.videoPoint, max: summary.videoTotal * 20, color: ui.pink },
        { id: 'test', value: summary.testPoint, max: summary.testTotal * 20, color: ui.pink },
        { id: 'conspect', value: summary.conspectPoint, max: summary.conspectMax, color: ui.pink },
        { id: 'zachet', value: summary.zachetPoint, max: 20, color: ui.pink },
        { id: 'practice', value: summary.practicePoint, max: 40, color: ui.pink },
    ]

    return (
        <Wrapper>
            <Toolbar className="no-print">
                <Breadcrumb links={[formatMessage({ id: 'rating' }), formatMessage({ id: 'weeklyReport' })]} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
                    {position >= 0 && (
                        <>
                            <Tooltip title={prev ? `${prev.firstName} ${prev.lastName}` : ''}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackRounded />}
                                        disabled={!prev}
                                        onClick={() => prev && goTo(prev.userSubjectEnrollId)}
                                    >
                                        {formatMessage({ id: 'reportPrev' })}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Box sx={{ fontSize: 14, fontWeight: 600, color: ui.muted, minWidth: 56, textAlign: 'center' }}>
                                {position + 1} / {siblings.length}
                            </Box>
                            <Tooltip title={next ? `${next.firstName} ${next.lastName}` : ''}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        endIcon={<ArrowForwardRounded />}
                                        disabled={!next}
                                        onClick={() => next && goTo(next.userSubjectEnrollId)}
                                    >
                                        {formatMessage({ id: 'reportNext' })}
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                <Button
                    variant="contained"
                    startIcon={<PrintRounded />}
                    onClick={() => window.print()}
                >
                    {formatMessage({ id: 'downloadPdf' })}
                </Button>
                </Box>
            </Toolbar>

            <Sheet id="report-sheet">
                <SheetHeader>
                    <Box>
                        <img
                            src={Logo}
                            alt="Dostyq Online"
                            style={{ width: 150 }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                        <ReportTitle>{formatMessage({ id: 'weeklyReport' })}</ReportTitle>
                        <Pink>{period}</Pink>
                    </Box>
                </SheetHeader>

                <ReportStudentCard
                    firstName={summary.firstName}
                    lastName={summary.lastName}
                    subtitle={`${summary.groupTitle}${report[`subjectTitle${capitalize(locale)}`] ? ` · ${report[`subjectTitle${capitalize(locale)}`]}` : ''}${mentorName ? ` · ${mentorName}` : ''}`}
                    totalPoint={summary.totalPoint}
                    maxPoint={summary.maxPoint}
                    rank={report.rank}
                />

                <Cards>
                    {cards.map((card) => (
                        <MetricCard
                            key={card.id}
                            color={card.color}
                        >
                            <Muted>{formatMessage({ id: card.id })}</Muted>
                            <MetricValue>
                                {card.value}
                                <small> / {card.max}</small>
                            </MetricValue>
                        </MetricCard>
                    ))}
                </Cards>

                <SectionTitle color={ui.pink}>
                    1. {formatMessage({ id: 'videoLessonsAndConspect' })}
                    <Badge>{formatMessage({ id: 'automatic' })}</Badge>
                </SectionTitle>

                <ReportTable>
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: 'topic' })}</th>
                            <th>{formatMessage({ id: 'watched' })}</th>
                            <th>{formatMessage({ id: 'conspect' })}</th>
                            <th className="right">{formatMessage({ id: 'score' })}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.videoRows.map((row, index) => (
                            <tr key={index}>
                                <td>{row.videoTitle || row.topicTitle}</td>
                                <td>{row.watchedPercent}%</td>
                                <td>{row.conspectPoint ? `+${row.conspectPoint}` : '-'}</td>
                                <td className="right">{row.point}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                            <td className="right">{videoPoint}</td>
                        </tr>
                    </tfoot>
                </ReportTable>

                <SectionTitle color={ui.pink}>
                    2. {formatMessage({ id: 'topicTestResults' })}
                    <Badge>{formatMessage({ id: 'automatic' })}</Badge>
                </SectionTitle>

                <ReportTable>
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: 'test' })}</th>
                            <th>{formatMessage({ id: 'correctAnswer' })}</th>
                            <th>%</th>
                            <th className="right">{formatMessage({ id: 'score' })}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.testRows.map((row, index) => (
                            <tr key={index}>
                                <td>{row.testTitle}</td>
                                <td>
                                    {row.point} / {row.maxPoint}
                                </td>
                                <td>{row.percent}%</td>
                                <td className="right">{row.ratingPoint}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                            <td className="right">{summary.testPoint}</td>
                        </tr>
                    </tfoot>
                </ReportTable>

                <SectionTitle color={ui.pink}>
                    3. {formatMessage({ id: 'practiceAttendance' })}
                    <Badge>{formatMessage({ id: 'systemAndMentor' })}</Badge>
                </SectionTitle>

                <PlainCard>
                    <Box sx={{ fontSize: '15px', fontWeight: 600, color: ui.ink }}>
                        {score?.practiceStatus
                            ? formatMessage({ id: PRACTICE_LABEL[score.practiceStatus] })
                            : formatMessage({ id: 'notMarked' })}
                    </Box>
                    <Muted>{summary.practicePoint} / 40</Muted>
                </PlainCard>

                <SectionTitle color={ui.pink}>
                    4. {formatMessage({ id: 'zachetGrade' })}
                    <Badge>{formatMessage({ id: 'mentorSets' })}</Badge>
                </SectionTitle>

                <ZachetRow>
                    <ZachetCard>
                        <Muted>{formatMessage({ id: 'zachet' })}</Muted>
                        <MetricValue>
                            {summary.zachetPoint}
                            <small> / 20</small>
                        </MetricValue>
                    </ZachetCard>

                    <PlainCard style={{ flexGrow: 1 }}>
                        <Muted>{formatMessage({ id: 'gradeScale' })}</Muted>
                        <ScaleTrack>
                            <ScaleFill style={{ width: `${(summary.zachetPoint / 20) * 100}%` }} />
                        </ScaleTrack>
                        <ScaleTicks>
                            <span>0</span>
                            <span>5</span>
                            <span>10</span>
                            <span>15</span>
                            <span>20</span>
                        </ScaleTicks>
                        <Comment>{score?.zachetComment || ''}</Comment>

                        {!readOnly && <EditRow className="no-print">
                            <TextField
                                type="number"
                                size="small"
                                label={formatMessage({ id: 'zachet' })}
                                value={zachetInput}
                                onChange={(e) => setZachetInput(e.target.value)}
                                inputProps={{ min: 0, max: 20 }}
                                sx={{ width: 130 }}
                            />
                            <Button
                                variant="contained"
                                onClick={saveZachet}
                                disabled={isSaving}
                            >
                                {formatMessage({ id: 'save' })}
                            </Button>
                        </EditRow>}
                    </PlainCard>
                </ZachetRow>

                <SectionTitle color={ui.pink}>
                    5. {formatMessage({ id: 'mentorFeedback' })}
                </SectionTitle>

                <FeedbackBox>
                    <Comment className="print-only">{score?.mentorComment || ''}</Comment>
                    <RuledLine className="print-only" />
                    <RuledLine className="print-only" />
                    <RuledLine className="print-only" />

                    {readOnly && <Comment className="no-print">{score?.mentorComment || ''}</Comment>}
                    {!readOnly && <Box className="no-print">
                        <TextField
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder={formatMessage({ id: 'mentorFeedback' })}
                            value={commentInput}
                            onChange={(e) => setCommentInput(e.target.value)}
                        />
                        <EditRow>
                            <Button
                                variant="contained"
                                onClick={saveComment}
                                disabled={isSaving}
                            >
                                {formatMessage({ id: 'save' })}
                            </Button>
                        </EditRow>
                    </Box>}
                </FeedbackBox>

                <SignatureRow>
                    <SignatureBox>
                        <Muted>{formatMessage({ id: 'mentorSignature' })}</Muted>
                    </SignatureBox>
                    <SignatureBox>
                        <Muted>{formatMessage({ id: 'reportDate' })}</Muted>
                    </SignatureBox>
                </SignatureRow>


                <Footnote>{formatMessage({ id: 'reportFootnote' })}</Footnote>
            </Sheet>
        </Wrapper>
    )
}

export default StudentReportPage

const Wrapper = styled.div`
    margin: 10px;

    .print-only {
        display: none;
    }

    @media print {
        margin: 0;

        .no-print {
            display: none !important;
        }

        .print-only {
            display: block;
        }
    }
`

const EditRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
`

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
`

const Sheet = styled.div`
    background: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 32px;
    max-width: 900px;
    margin: 0 auto;

    @media print {
        border: none;
        border-radius: 0;
        padding: 0;
        max-width: none;
    }
`

const SheetHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 3px solid ${ui.pink};
`

const ReportTitle = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${ui.ink};
`

const Muted = styled.div`
    font-size: 12px;
    color: ${ui.muted};
`

const Pink = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${ui.pink};
`






const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin: 20px 0 8px;

    @media screen and (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const MetricCard = styled.div<{ color: string }>`
    padding: 14px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    border-top: 3px solid ${({ color }) => color};
`

const MetricValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${ui.ink};

    small {
        font-size: 12px;
        color: ${ui.muted};
    }
`

const SectionTitle = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 12px;
    padding-left: 10px;
    border-left: 4px solid ${({ color }) => color};
    font-size: 15px;
    font-weight: 700;
    color: ${ui.ink};
`

const Badge = styled.span`
    margin-left: auto;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${ui.sub};
    background: ${ui.track};
`

const ReportTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        padding: 10px 12px;
        background: ${ui.surface};
        color: ${ui.muted};
        font-weight: 600;
        font-size: 12px;
    }

    td {
        padding: 10px 12px;
        border-top: 1px solid ${ui.border};
        color: ${ui.ink};
    }

    tfoot td {
        font-weight: 700;
        background: ${ui.surface};
    }

    .right {
        text-align: right;
    }
`

const PlainCard = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
`

const ZachetRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: stretch;
    flex-wrap: wrap;
`

const ZachetCard = styled.div`
    min-width: 160px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    background: ${ui.surface};
    text-align: center;
`

const ScaleTrack = styled.div`
    height: 10px;
    margin: 10px 0 6px;
    border-radius: 100px;
    background: ${ui.track};
    overflow: hidden;
`

const ScaleFill = styled.div`
    height: 100%;
    border-radius: 100px;
    background: ${ui.pink};
`

const ScaleTicks = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: ${ui.muted};
`

const Comment = styled.div`
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.6;
    color: ${ui.ink};
    white-space: pre-wrap;
`

const FeedbackBox = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
`

const RuledLine = styled.div`
    height: 28px;
    border-bottom: 1px solid ${ui.border};
`

const SignatureRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 24px;
`

const SignatureBox = styled.div`
    padding-top: 28px;
    border-top: 1px solid ${ui.ink};
`





const Footnote = styled.div`
    margin-top: 12px;
    font-size: 11px;
    color: ${ui.muted};
`
